import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Loader2,
  Upload,
  ChevronLeft,
  ArrowRight,
  Mail,
  LogIn,
  Plus,
  Check,
  Car
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_FILE_SIZE = 2.5 * 1024 * 1024;

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const formatApiError = (detail) => {
  if (detail == null) return 'Something went wrong.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(' ');
  return String(detail);
};

const RallyRegister = () => {
  const { rallyId } = useParams();
  const navigate = useNavigate();
  const { user, login, register } = useAuth();

  const [rally, setRally] = useState(null);
  const [loadingRally, setLoadingRally] = useState(true);

  // mode: 'new' (default for guests) | 'returning' (login modal-ish) | 'logged-in'
  const [mode, setMode] = useState(user ? 'logged-in' : 'new');
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  // New customer form
  const [newForm, setNewForm] = useState({
    name: '',
    email: '',
    phone: '',
    instagram_handle: '',
    car_year: '',
    car_make: '',
    car_model: '',
    car_color: '',
    car_mods: '',
    photo_base64: '',
    password: '',
    confirm: ''
  });

  // Returning customer form
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });

  // Logged-in flow: existing vehicles
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicleId, setSelectedVehicleId] = useState('');
  const [loadingVehicles, setLoadingVehicles] = useState(false);

  // Fetch rally
  useEffect(() => {
    axios
      .get(`${API_URL}/api/rallies/${rallyId}`)
      .then((r) => setRally(r.data))
      .catch(() => toast.error('Rally not found'))
      .finally(() => setLoadingRally(false));
  }, [rallyId]);

  // Sync mode with auth state
  useEffect(() => {
    if (user) setMode('logged-in');
    else if (mode === 'logged-in') setMode('new');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // When logged in, load their vehicles
  useEffect(() => {
    if (!user) return;
    setLoadingVehicles(true);
    axios
      .get(`${API_URL}/api/account/vehicles`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      })
      .then((res) => {
        setVehicles(res.data);
        if (res.data.length > 0) setSelectedVehicleId(res.data[0].id);
      })
      .catch(() => {})
      .finally(() => setLoadingVehicles(false));
  }, [user]);

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      toast.error('Image must be under 2.5MB');
      return;
    }
    try {
      const dataUri = await fileToBase64(file);
      setNewForm((f) => ({ ...f, photo_base64: dataUri }));
    } catch {
      toast.error('Failed to read image');
    }
  };

  /** Create rally signup + Stripe checkout session, then redirect to Stripe. */
  const finalizeRegistrationAndPay = async (vehicleId) => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    // Create rally signup
    await axios.post(
      `${API_URL}/api/rally-signups`,
      { rally_id: rallyId, vehicle_id: vehicleId },
      { headers }
    );

    // Create Stripe checkout
    const checkout = await axios.post(
      `${API_URL}/api/payments/checkout`,
      { rally_id: rallyId, origin_url: window.location.origin },
      { headers }
    );
    window.location.href = checkout.data.url;
  };

  const handleNewSubmit = async (e) => {
    e.preventDefault();
    if (newForm.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (newForm.password !== newForm.confirm) {
      toast.error('Passwords do not match');
      return;
    }
    if (!newForm.car_year || !newForm.car_make || !newForm.car_model || !newForm.car_color) {
      toast.error('Please complete your vehicle details');
      return;
    }

    setSubmitting(true);
    try {
      // 1. Create account
      await register(newForm.email, newForm.password, newForm.name, newForm.phone);

      // 2. Save instagram handle to profile if provided (optional – stored later via signup record)
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // 3. Add vehicle
      const vehiclePayload = {
        year: parseInt(newForm.car_year, 10),
        make: newForm.car_make.trim(),
        model: newForm.car_model.trim(),
        color: newForm.car_color.trim(),
        mods: newForm.car_mods.trim() || null,
        photo_base64: newForm.photo_base64 || null
      };
      const vehicleRes = await axios.post(`${API_URL}/api/account/vehicles`, vehiclePayload, {
        headers
      });

      // 4. Rally signup + Stripe (sends instagram_handle on signup record)
      await axios.post(
        `${API_URL}/api/rally-signups`,
        {
          rally_id: rallyId,
          vehicle_id: vehicleRes.data.id,
          instagram_handle: newForm.instagram_handle || null
        },
        { headers }
      );
      const checkout = await axios.post(
        `${API_URL}/api/payments/checkout`,
        { rally_id: rallyId, origin_url: window.location.origin },
        { headers }
      );
      window.location.href = checkout.data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Sign-up failed');
      setSubmitting(false);
    }
  };

  const handleReturningSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(loginForm.email, loginForm.password);
      toast.success('Welcome back!');
      // The useEffect will switch mode to 'logged-in'
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Login failed');
      setSubmitting(false);
    }
  };

  const handleLoggedInConfirm = async () => {
    if (!selectedVehicleId) {
      toast.error('Please select a vehicle or add one to your garage first');
      return;
    }
    setSubmitting(true);
    try {
      await finalizeRegistrationAndPay(selectedVehicleId);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Failed to register');
      setSubmitting(false);
    }
  };

  if (loadingRally) {
    return (
      <div className="min-h-screen bg-[#0A0A0A]">
        <Navigation />
        <div className="pt-32 text-center">
          <Loader2 className="w-8 h-8 text-[#E8651A] animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  if (!rally) {
    return (
      <div className="min-h-screen bg-[#0A0A0A]">
        <Navigation />
        <div className="pt-32 text-center">
          <p className="text-[#A19D98]">Rally not found.</p>
          <Link to="/rallies" className="btn-primary inline-flex mt-4">View Rallies</Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div data-testid="rally-register-page" className="min-h-screen bg-[#0A0A0A]">
      <Navigation />

      <section className="pt-28 pb-16 px-6">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <Link
            to={`/rallies/${rallyId}`}
            className="text-[#A19D98] hover:text-[#E8651A] flex items-center gap-2 mb-6 text-sm"
          >
            <ChevronLeft size={16} /> Back to rally
          </Link>

          <div className="mb-8">
            <p className="text-[#E8651A] font-['Oswald'] tracking-wider text-sm mb-2">
              REGISTER FOR
            </p>
            <h1 className="font-['Oswald'] text-3xl sm:text-4xl text-[#F2EDE6] tracking-wider">
              {rally.name.toUpperCase()}
            </h1>
            <p className="text-[#A19D98] text-sm mt-2">
              {rally.dates}
              {rally.price && rally.price !== 'Pricing revealed after sign-up' && (
                <span className="text-[#F2EDE6]"> · {rally.price}</span>
              )}
            </p>
          </div>

          {/* LOGGED-IN flow */}
          {mode === 'logged-in' && user && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-[#141414] border border-[#2A2A2A] p-6 sm:p-8"
              data-testid="register-loggedin"
            >
              <div className="flex items-center gap-3 text-[#34C759] mb-6">
                <Check size={18} />
                <p className="text-sm">
                  Signed in as <span className="text-[#F2EDE6]">{user.name}</span> ({user.email})
                </p>
              </div>

              <h2 className="font-['Oswald'] text-xl text-[#F2EDE6] tracking-wider mb-4">
                CHOOSE YOUR RIDE
              </h2>

              {loadingVehicles ? (
                <Loader2 className="w-6 h-6 text-[#E8651A] animate-spin" />
              ) : vehicles.length === 0 ? (
                <div className="bg-[#0A0A0A] border border-dashed border-[#2A2A2A] p-6 text-center">
                  <Car className="w-10 h-10 text-[#2A2A2A] mx-auto mb-3" />
                  <p className="text-[#A19D98] text-sm mb-3">
                    You don't have a vehicle saved yet. Add one to register.
                  </p>
                  <Link
                    to="/account/vehicles"
                    className="btn-primary inline-flex items-center gap-2 text-sm"
                  >
                    <Plus size={14} /> Add Vehicle
                  </Link>
                </div>
              ) : (
                <>
                  <Select value={selectedVehicleId} onValueChange={setSelectedVehicleId}>
                    <SelectTrigger
                      data-testid="register-vehicle-select"
                      className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2EDE6]"
                    >
                      <SelectValue placeholder="Select your ride" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#141414] border-[#2A2A2A]">
                      {vehicles.map((v) => (
                        <SelectItem key={v.id} value={v.id} className="text-[#F2EDE6]">
                          {v.year} {v.make} {v.model} ({v.color})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Link
                    to="/account/vehicles"
                    className="text-[#E8651A] text-xs hover:underline mt-3 inline-block"
                  >
                    Add a different vehicle →
                  </Link>

                  <button
                    onClick={handleLoggedInConfirm}
                    disabled={submitting}
                    data-testid="register-confirm-btn"
                    className="btn-primary w-full mt-6 flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {submitting ? (
                      <Loader2 size={18} className="animate-spin" />
                    ) : (
                      <>
                        Continue to Payment <ArrowRight size={18} />
                      </>
                    )}
                  </button>
                </>
              )}
            </motion.div>
          )}

          {/* RETURNING flow */}
          {mode === 'returning' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-[#141414] border border-[#2A2A2A] p-6 sm:p-8"
              data-testid="register-returning"
            >
              <h2 className="font-['Oswald'] text-xl text-[#F2EDE6] tracking-wider mb-2">
                WELCOME BACK
              </h2>
              <p className="text-[#A19D98] text-sm mb-6">
                Log in with the email from your last rally to skip the forms.
              </p>

              <form onSubmit={handleReturningSubmit} className="space-y-4">
                <div>
                  <label className="block text-[#A19D98] text-sm mb-2">Email</label>
                  <Input
                    type="email"
                    value={loginForm.email}
                    onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                    required
                    data-testid="returning-email-input"
                    className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2EDE6]"
                  />
                </div>
                <div>
                  <label className="block text-[#A19D98] text-sm mb-2">Password</label>
                  <Input
                    type="password"
                    value={loginForm.password}
                    onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                    required
                    data-testid="returning-password-input"
                    className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2EDE6]"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="returning-login-btn"
                  className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {submitting ? <Loader2 size={18} className="animate-spin" /> : (
                    <><LogIn size={18} /> Log in & Continue</>
                  )}
                </button>
              </form>

              <button
                type="button"
                onClick={() => setMode('new')}
                className="text-[#A19D98] hover:text-[#E8651A] text-sm mt-6 w-full text-center"
              >
                First time? Sign up instead →
              </button>
            </motion.div>
          )}

          {/* NEW customer flow */}
          {mode === 'new' && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid="register-new"
            >
              {/* Returning callout */}
              <button
                type="button"
                onClick={() => setMode('returning')}
                data-testid="register-returning-toggle"
                className="w-full bg-[#141414] border border-[#E8651A]/30 hover:border-[#E8651A] p-4 mb-6 flex items-center justify-between transition-colors"
              >
                <div className="flex items-center gap-3 text-left">
                  <Mail className="text-[#E8651A]" size={20} />
                  <div>
                    <p className="text-[#F2EDE6] font-['Oswald'] tracking-wider text-sm">
                      DONE A RALLY WITH US ALREADY?
                    </p>
                    <p className="text-[#A19D98] text-xs">
                      Use the email from your last rally — skip the forms.
                    </p>
                  </div>
                </div>
                <ArrowRight className="text-[#E8651A] flex-shrink-0" size={18} />
              </button>

              <form onSubmit={handleNewSubmit} className="bg-[#141414] border border-[#2A2A2A] p-6 sm:p-8 space-y-5">
                <div>
                  <h2 className="font-['Oswald'] text-xl text-[#F2EDE6] tracking-wider mb-1">
                    YOUR DETAILS
                  </h2>
                  <p className="text-[#A19D98] text-xs">We'll create your rally account from this.</p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[#A19D98] text-xs mb-1">Full Name *</label>
                    <Input
                      value={newForm.name}
                      onChange={(e) => setNewForm({ ...newForm, name: e.target.value })}
                      required
                      data-testid="new-name-input"
                      className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2EDE6]"
                    />
                  </div>
                  <div>
                    <label className="block text-[#A19D98] text-xs mb-1">Email *</label>
                    <Input
                      type="email"
                      value={newForm.email}
                      onChange={(e) => setNewForm({ ...newForm, email: e.target.value })}
                      required
                      data-testid="new-email-input"
                      className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2EDE6]"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[#A19D98] text-xs mb-1">Phone *</label>
                    <Input
                      type="tel"
                      value={newForm.phone}
                      onChange={(e) => setNewForm({ ...newForm, phone: e.target.value })}
                      required
                      data-testid="new-phone-input"
                      className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2EDE6]"
                    />
                  </div>
                  <div>
                    <label className="block text-[#A19D98] text-xs mb-1">Instagram (optional)</label>
                    <Input
                      value={newForm.instagram_handle}
                      onChange={(e) => setNewForm({ ...newForm, instagram_handle: e.target.value })}
                      placeholder="@yourhandle"
                      data-testid="new-instagram-input"
                      className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2EDE6]"
                    />
                  </div>
                </div>

                <hr className="border-[#2A2A2A]" />

                <div>
                  <h2 className="font-['Oswald'] text-xl text-[#F2EDE6] tracking-wider mb-1">YOUR CAR</h2>
                  <p className="text-[#A19D98] text-xs">We'll save this to your garage for future rallies.</p>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-[#A19D98] text-xs mb-1">Year *</label>
                    <Input
                      type="number"
                      value={newForm.car_year}
                      onChange={(e) => setNewForm({ ...newForm, car_year: e.target.value })}
                      required
                      placeholder="2019"
                      data-testid="new-car-year-input"
                      className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2EDE6]"
                    />
                  </div>
                  <div>
                    <label className="block text-[#A19D98] text-xs mb-1">Make *</label>
                    <Input
                      value={newForm.car_make}
                      onChange={(e) => setNewForm({ ...newForm, car_make: e.target.value })}
                      required
                      placeholder="Toyota"
                      data-testid="new-car-make-input"
                      className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2EDE6]"
                    />
                  </div>
                  <div>
                    <label className="block text-[#A19D98] text-xs mb-1">Model *</label>
                    <Input
                      value={newForm.car_model}
                      onChange={(e) => setNewForm({ ...newForm, car_model: e.target.value })}
                      required
                      placeholder="GR86"
                      data-testid="new-car-model-input"
                      className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2EDE6]"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[#A19D98] text-xs mb-1">Color *</label>
                    <Input
                      value={newForm.car_color}
                      onChange={(e) => setNewForm({ ...newForm, car_color: e.target.value })}
                      required
                      data-testid="new-car-color-input"
                      className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2EDE6]"
                    />
                  </div>
                  <div>
                    <label className="block text-[#A19D98] text-xs mb-1">Mods (optional)</label>
                    <Input
                      value={newForm.car_mods}
                      onChange={(e) => setNewForm({ ...newForm, car_mods: e.target.value })}
                      placeholder="e.g. coilovers, wrap"
                      className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2EDE6]"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[#A19D98] text-xs mb-1">Car Photo (optional)</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="file"
                      accept="image/*"
                      ref={fileRef}
                      onChange={handlePhotoChange}
                      data-testid="new-photo-input"
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => fileRef.current?.click()}
                      className="btn-secondary flex items-center gap-2 text-sm"
                    >
                      <Upload size={14} /> {newForm.photo_base64 ? 'Change Photo' : 'Upload Photo'}
                    </button>
                    {newForm.photo_base64 && (
                      <img src={newForm.photo_base64} alt="preview" className="w-16 h-16 object-cover border border-[#2A2A2A]" />
                    )}
                  </div>
                </div>

                <hr className="border-[#2A2A2A]" />

                <div>
                  <h2 className="font-['Oswald'] text-xl text-[#F2EDE6] tracking-wider mb-1">SET A PASSWORD</h2>
                  <p className="text-[#A19D98] text-xs">
                    So you can register for future rallies in seconds.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[#A19D98] text-xs mb-1">Password * (8+ chars)</label>
                    <Input
                      type="password"
                      value={newForm.password}
                      onChange={(e) => setNewForm({ ...newForm, password: e.target.value })}
                      required
                      minLength={8}
                      data-testid="new-password-input"
                      className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2EDE6]"
                    />
                  </div>
                  <div>
                    <label className="block text-[#A19D98] text-xs mb-1">Confirm *</label>
                    <Input
                      type="password"
                      value={newForm.confirm}
                      onChange={(e) => setNewForm({ ...newForm, confirm: e.target.value })}
                      required
                      data-testid="new-confirm-input"
                      className="bg-[#0A0A0A] border-[#2A2A2A] text-[#F2EDE6]"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="new-submit-btn"
                  className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {submitting ? (
                    <Loader2 size={18} className="animate-spin" />
                  ) : (
                    <>
                      Continue to Payment <ArrowRight size={18} />
                    </>
                  )}
                </button>
              </form>
            </motion.div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default RallyRegister;
